// Story 16.5 — aceptar una invitación. Pública (fuera del AppShell): quien
// recibe el link puede no tener cuenta todavía.
//
// El token llega en el fragmento (`/invite#<token>`, ver `lib/hash-token`): si
// primero hay que iniciar sesión, `/login?redirect=/invite` vuelve acá sin que
// el token pase por ninguna URL (ni por los logs del servidor).

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMe } from '@/hooks/queries/use-me';
import { ApiError, api } from '@/lib/api-client';
import { forgetHashToken, readHashToken } from '@/lib/hash-token';
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from '@/lib/organization-roles';
import type { OrganizationRole } from '@/lib/permissions';

const STORAGE_KEY = 'revel.invite-token';

interface InvitationInfo {
  organization: { id: number; name: string };
  email: string;
  role: OrganizationRole;
  expiresAt: string;
  accountExists: boolean;
}

const readToken = () => readHashToken(STORAGE_KEY);
const forgetToken = () => forgetHashToken(STORAGE_KEY);

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function Shell(props: {
  title: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{props.title}</CardTitle>
          {props.description && <CardDescription>{props.description}</CardDescription>}
        </CardHeader>
        {props.children}
      </Card>
    </div>
  );
}

function SignupForm(props: { token: string; info: InvitationInfo }): React.ReactElement {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (password.length < 8) return setError('La contraseña necesita al menos 8 caracteres.');
    if (password !== confirm) return setError('Las contraseñas no coinciden.');
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/api/v1/invitations/signup', {
        token: props.token,
        password,
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      setPassword('');
      setConfirm('');
      forgetToken();
      // La sesión es nueva: nada de la anterior debe quedar en caché.
      qc.clear();
      toast.success(`Ya eres parte de ${props.info.organization.name}`);
      await navigate({ to: '/' });
    } catch (err) {
      setError(errorMessage(err, 'No se pudo crear la cuenta.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="invite-email">Email</Label>
          <Input id="invite-email" value={props.info.email} readOnly disabled />
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-name">Nombre</Label>
          <Input
            id="invite-name"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-password">Contraseña</Label>
          <Input
            id="invite-password"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            maxLength={72}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="invite-confirm">Repite la contraseña</Label>
          <Input
            id="invite-confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            maxLength={72}
          />
        </div>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </CardContent>
      <CardFooter>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Creando cuenta…' : 'Crear cuenta y unirme'}
        </Button>
      </CardFooter>
    </form>
  );
}

function ExistingAccount(props: { token: string; info: InvitationInfo }): React.ReactElement {
  const { data: me, isLoading } = useMe();
  const [submitting, setSubmitting] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toLogin = () => navigate({ to: '/login', search: { redirect: '/invite' } as never });

  async function accept(): Promise<void> {
    setSubmitting(true);
    try {
      await api.post('/api/v1/invitations/accept', { token: props.token });
      forgetToken();
      await qc.invalidateQueries();
      toast.success(`Ya eres parte de ${props.info.organization.name}`);
      await navigate({ to: '/' });
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo aceptar la invitación'));
    } finally {
      setSubmitting(false);
    }
  }

  async function switchAccount(): Promise<void> {
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      // La cookie puede haber vencido ya.
    }
    qc.clear();
    await toLogin();
  }

  if (isLoading)
    return <CardContent className="text-sm text-muted-foreground">Cargando…</CardContent>;
  if (!me) {
    return (
      <>
        <CardContent className="text-sm">
          Ya hay una cuenta con <strong>{props.info.email}</strong>. Inicia sesión con ella para
          unirte.
        </CardContent>
        <CardFooter>
          <Button className="w-full" onClick={() => void toLogin()}>
            Iniciar sesión
          </Button>
        </CardFooter>
      </>
    );
  }
  if (me.email !== props.info.email) {
    return (
      <>
        <CardContent className="text-sm">
          Iniciaste sesión como <strong>{me.email}</strong>, pero la invitación es para{' '}
          <strong>{props.info.email}</strong>.
        </CardContent>
        <CardFooter>
          <Button className="w-full" variant="outline" onClick={() => void switchAccount()}>
            Cerrar sesión y entrar con {props.info.email}
          </Button>
        </CardFooter>
      </>
    );
  }
  return (
    <CardFooter>
      <Button className="w-full" disabled={submitting} onClick={() => void accept()}>
        {submitting ? 'Uniéndote…' : `Unirme a ${props.info.organization.name}`}
      </Button>
    </CardFooter>
  );
}

export function InvitePage(): React.ReactElement {
  const [token] = useState(readToken);
  const lookup = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.post<InvitationInfo>('/api/v1/invitations/lookup', { token }),
    enabled: Boolean(token),
    retry: false,
  });

  if (!token)
    return <Shell title="Link inválido" description="Este link de invitación no sirve." />;
  if (lookup.isLoading) return <Shell title="Invitación" description="Cargando invitación…" />;
  if (lookup.isError || !lookup.data) {
    const gone = lookup.error instanceof ApiError && lookup.error.status === 404;
    return (
      <Shell
        title="Invitación no disponible"
        description={
          gone
            ? 'La invitación ya se usó, se revocó o venció. Pide un link nuevo a quien te invitó.'
            : 'No se pudo cargar la invitación. Inténtalo de nuevo en un momento.'
        }
      />
    );
  }

  const info = lookup.data;
  return (
    <Shell
      title={`Únete a ${info.organization.name}`}
      description={
        <>
          Te invitaron como <strong>{ROLE_LABELS[info.role]}</strong>:{' '}
          {ROLE_DESCRIPTIONS[info.role].toLowerCase()}.
        </>
      }
    >
      {info.accountExists ? (
        <ExistingAccount token={token} info={info} />
      ) : (
        <SignupForm token={token} info={info} />
      )}
    </Shell>
  );
}
